import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Loader2, Euro, CreditCard, Clock, CheckCircle2, AlertCircle,
  MinusCircle, ArrowLeft, ChevronRight, Search, Users, FolderOpen,
  FolderClosed, FileWarning, FileCheck2, BarChart3, MessageSquare,
  ClipboardList, UserCircle2, Activity, Printer, Filter
} from "lucide-react";
import api from "@/api";
import { usePagination } from "@/lib/usePagination";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";

const MONTHS = [
  { value: "all", label: "Tutti i mesi" },
  { value: "1", label: "Gennaio" }, { value: "2", label: "Febbraio" },
  { value: "3", label: "Marzo" }, { value: "4", label: "Aprile" },
  { value: "5", label: "Maggio" }, { value: "6", label: "Giugno" },
  { value: "7", label: "Luglio" }, { value: "8", label: "Agosto" },
  { value: "9", label: "Settembre" }, { value: "10", label: "Ottobre" },
  { value: "11", label: "Novembre" }, { value: "12", label: "Dicembre" },
];

const fmt = (v) => (v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("it-IT"); } catch { return d; }
};

const STATUS_BADGE = {
  pagata: { label: "Pagata", cls: "bg-green-100 text-green-700" },
  parziale: { label: "Parziale", cls: "bg-orange-100 text-orange-700" },
  non_pagata: { label: "Non pagata", cls: "bg-red-100 text-red-700" },
};

function ReportTab({ clients = [], isAdmin = true }) {
  const [invoiceData, setInvoiceData] = useState(null);
  const [clientInvData, setClientInvData] = useState([]);
  const [practiceData, setPracticeData] = useState(null);
  const [clientPracData, setClientPracData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState("all");
  const [management, setManagement] = useState("agenzia");
  const [invClientSearch, setInvClientSearch] = useState("");
  const [pracClientSearch, setPracClientSearch] = useState("");

  // Drill-down
  const [drillDown, setDrillDown] = useState(null); // { title, type: 'invoice'|'practice' }
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // 27/02/2026 — Report Cliente: KPI aggregato per singolo cliente.
  // Stato indipendente dal drill-down classico per non interferire con
  // il flusso esistente (admin). Accessibile a TUTTI i ruoli.
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientReport, setClientReport] = useState(null);
  const [clientReportLoading, setClientReportLoading] = useState(false);

  // 27/02/2026 — Filtri periodo specifici per il Report Cliente.
  // Stato draft (input live) vs applicato (trigger fetch). L'utente clicca
  // "Applica filtro" per consolidare; cambio cliente applica direttamente.
  const [crMonth, setCrMonth] = useState("all");   // "all" | "1".."12"
  const [crYear, setCrYear] = useState("all");     // "all" | "2024".."2026"
  const [crFromInput, setCrFromInput] = useState("");
  const [crToInput, setCrToInput] = useState("");
  const [appliedPeriod, setAppliedPeriod] = useState({ from: "", to: "" });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  const activeFilters = useCallback(() => ({
    year: year !== "all" ? year : null,
    month: month !== "all" ? month : null,
    management: management !== "all" ? management : null,
  }), [year, month, management]);

  const load = useCallback(async () => {
    // Non-admin: salta i 4 endpoint aggregati globali (richiedono ruolo
    // amministratore). Vedrà solo la sezione "Report Cliente".
    if (!isAdmin) {
      setLoading(false);
      setInvoiceData(null);
      setPracticeData(null);
      return;
    }
    setLoading(true);
    try {
      const f = activeFilters();
      const [invSummary, invByClient, pracSummary, pracByClient] = await Promise.all([
        api.getInvoicesSummary(f.year, f.month, f.management).catch(() => null),
        api.getInvoicesByClient(f.year, f.month, f.management).catch(() => []),
        api.getPracticesSummary(f.year, f.month, f.management).catch(() => null),
        api.getPracticesByClient(f.year, f.month, f.management).catch(() => []),
      ]);
      setInvoiceData(invSummary);
      setClientInvData(invByClient);
      setPracticeData(pracSummary);
      setClientPracData(pracByClient);
    } catch (e) {
      console.error("Report load error:", e);
    } finally {
      setLoading(false);
    }
  }, [activeFilters, isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setDrillDown(null); }, [year, month, management]);

  // 27/02/2026 — Carica il Report Cliente quando l'utente seleziona un
  // cliente o cambia il periodo applicato. Endpoint dedicato accessibile
  // a tutti i ruoli, dati live, filtri periodo server-side.
  useEffect(() => {
    if (!selectedClientId) {
      setClientReport(null);
      return;
    }
    let cancelled = false;
    setClientReportLoading(true);
    api.getClientReport(selectedClientId, appliedPeriod)
      .then((data) => { if (!cancelled) setClientReport(data); })
      .catch((err) => {
        console.error("getClientReport failed:", err);
        if (!cancelled) setClientReport(null);
      })
      .finally(() => { if (!cancelled) setClientReportLoading(false); });
    return () => { cancelled = true; };
  }, [selectedClientId, appliedPeriod]);

  // Lista clienti normalizzata per il combobox searchable (single-line UX)
  const clientComboItems = useMemo(() => {
    const list = Array.isArray(clients) ? clients : [];
    return list.map((c) => {
      const label = c.fullName || c.companyName || c.name || c.id;
      const taxCode = c.taxCode || c.codiceFiscale || "";
      const vat = c.vatNumber || c.partitaIVA || "";
      return {
        value: c.id,
        label,
        searchTerms: [label, taxCode, vat].filter(Boolean).join(" "),
      };
    });
  }, [clients]);

  // ── Helpers periodo Report Cliente ──────────────────────────────────
  // Mese/anno → range [from, to]. Se solo anno: 1 gen → 31 dic. Se solo mese,
  // implicito anno corrente. Se anche dal/al impostati: prevalgono.
  const computeAppliedPeriod = useCallback(() => {
    // Date libere prevalgono
    if (crFromInput || crToInput) {
      return { from: crFromInput || "", to: crToInput || "" };
    }
    const y = crYear !== "all" ? parseInt(crYear, 10) : null;
    const m = crMonth !== "all" ? parseInt(crMonth, 10) : null;
    if (!y && !m) return { from: "", to: "" };
    const yy = y || new Date().getFullYear();
    if (m) {
      const from = `${yy}-${String(m).padStart(2, "0")}-01`;
      const lastDay = new Date(yy, m, 0).getDate();
      const to = `${yy}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      return { from, to };
    }
    return { from: `${yy}-01-01`, to: `${yy}-12-31` };
  }, [crMonth, crYear, crFromInput, crToInput]);

  const applyPeriod = useCallback(() => {
    setAppliedPeriod(computeAppliedPeriod());
  }, [computeAppliedPeriod]);

  const resetPeriod = useCallback(() => {
    setCrMonth("all");
    setCrYear("all");
    setCrFromInput("");
    setCrToInput("");
    setAppliedPeriod({ from: "", to: "" });
  }, []);

  // Etichetta periodo per UI e PDF
  const periodLabel = useMemo(() => {
    const { from, to } = appliedPeriod || {};
    if (!from && !to) return "Tutto lo storico";
    if (from && to) return `Dal ${fmtDate(from)} al ${fmtDate(to)}`;
    if (from) return `Da ${fmtDate(from)}`;
    return `Fino al ${fmtDate(to)}`;
  }, [appliedPeriod]);

  // ── Stampa scheda cliente ───────────────────────────────────────────
  const handlePrintClientReport = useCallback(() => {
    if (!clientReport) return;
    const r = clientReport;
    const selClient = (Array.isArray(clients) ? clients : []).find((c) => c.id === selectedClientId) || {};
    const today = new Date().toLocaleDateString("it-IT");
    const safeName = r.clientName || selClient.fullName || selClient.companyName || "Cliente";
    const taxCode = selClient.taxCode || selClient.codiceFiscale || "";
    const vat = selClient.vatNumber || selClient.partitaIVA || "";
    const email = selClient.email || "";
    const phone = selClient.phone || selClient.telefono || "";
    const address = [selClient.address, selClient.city, selClient.cap, selClient.province]
      .filter(Boolean).join(", ");
    const practices = r.practices || { total: 0, open: 0, closed: 0, items: [] };
    const diary = r.dailyDiary || { totalNotes: 0, lastNote: null };
    const work = r.periodicWork || { totalActivities: 0, lastActivity: null };
    const operators = Array.isArray(r.operators) ? r.operators : [];

    const printContent = `<!DOCTYPE html><html><head><title>Scheda Cliente — ${safeName}</title>
<style>
  body{font-family:Arial,sans-serif;padding:24px;font-size:12px;color:#1f2937;}
  h1{font-size:20px;margin:0 0 4px;color:#0f172a;}
  h2{font-size:13px;margin:18px 0 6px;border-bottom:1px solid #cbd5e1;padding-bottom:3px;color:#334155;text-transform:uppercase;letter-spacing:.5px;}
  .meta{color:#64748b;font-size:11px;margin-bottom:14px;}
  table{width:100%;border-collapse:collapse;font-size:11px;margin-top:6px;}
  th,td{border:1px solid #d1d5db;padding:5px 7px;text-align:left;vertical-align:top;}
  th{background:#f1f5f9;font-weight:600;}
  .kpi{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-top:6px;}
  .kpi .c{border:1px solid #e2e8f0;border-radius:4px;padding:8px;text-align:center;}
  .kpi .l{font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:.4px;}
  .kpi .v{font-size:18px;font-weight:700;color:#0f172a;margin-top:2px;}
  .ana td:first-child{background:#f8fafc;font-weight:600;width:30%;}
  .last{background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;padding:8px;margin-top:6px;font-size:11px;}
  .last .d{color:#64748b;font-size:10px;margin-bottom:3px;}
  .ops{display:flex;flex-wrap:wrap;gap:4px;margin-top:4px;}
  .ops span{border:1px solid #cbd5e1;border-radius:10px;padding:2px 8px;font-size:10px;background:#f8fafc;}
  @media print{body{padding:8mm;}}
</style></head><body>
  <h1>Scheda Cliente — ${safeName}</h1>
  <p class="meta">Periodo: ${periodLabel} &nbsp;|&nbsp; Stampato il ${today}</p>

  <h2>Dati anagrafici</h2>
  <table class="ana">
    <tr><td>Ragione sociale / Nominativo</td><td>${safeName}</td></tr>
    ${taxCode ? `<tr><td>Codice Fiscale</td><td>${taxCode}</td></tr>` : ""}
    ${vat ? `<tr><td>Partita IVA</td><td>${vat}</td></tr>` : ""}
    ${address ? `<tr><td>Indirizzo</td><td>${address}</td></tr>` : ""}
    ${email ? `<tr><td>Email</td><td>${email}</td></tr>` : ""}
    ${phone ? `<tr><td>Telefono</td><td>${phone}</td></tr>` : ""}
  </table>

  <h2>Riepilogo operativo</h2>
  <div class="kpi">
    <div class="c"><div class="l">Pratiche totali</div><div class="v">${practices.total}</div></div>
    <div class="c"><div class="l">In corso</div><div class="v">${practices.open}</div></div>
    <div class="c"><div class="l">Concluse</div><div class="v">${practices.closed}</div></div>
    <div class="c"><div class="l">Note Diario</div><div class="v">${diary.totalNotes}</div></div>
    <div class="c"><div class="l">Lavoro Quotidiano</div><div class="v">${work.totalActivities}</div></div>
  </div>

  <h2>Ultima nota Diario Giornaliero</h2>
  ${diary.lastNote ? `
    <div class="last">
      <div class="d">${fmtDate(diary.lastNote.date)}${diary.lastNote.time ? " &middot; " + diary.lastNote.time : ""}${diary.lastNote.contactType ? " &middot; " + diary.lastNote.contactType : ""}${diary.lastNote.operatorName ? " &middot; " + diary.lastNote.operatorName : ""}</div>
      <div>${(diary.lastNote.content || "—").replace(/</g, "&lt;")}</div>
    </div>` : '<p style="color:#94a3b8;font-size:11px;">Nessuna nota registrata nel periodo selezionato.</p>'}

  <h2>Ultima attività Lavoro Quotidiano</h2>
  ${work.lastActivity ? `
    <div class="last">
      <div class="d">${fmtDate(work.lastActivity.date)}${work.lastActivity.time ? " &middot; " + work.lastActivity.time : ""}${work.lastActivity.operationalArea ? " &middot; " + work.lastActivity.operationalArea : ""}${work.lastActivity.status ? " &middot; " + work.lastActivity.status : ""}${work.lastActivity.operatorName ? " &middot; " + work.lastActivity.operatorName : ""}</div>
      <div>${(work.lastActivity.description || "—").replace(/</g, "&lt;")}</div>
    </div>` : '<p style="color:#94a3b8;font-size:11px;">Nessuna attività registrata nel periodo selezionato.</p>'}

  ${operators.length > 0 ? `<h2>Operatori coinvolti (${operators.length})</h2>
    <div class="ops">${operators.map(o => `<span>${o.name}</span>`).join("")}</div>` : ""}

  <h2>Elenco pratiche (${practices.items.length})</h2>
  ${practices.items.length === 0
    ? '<p style="color:#94a3b8;font-size:11px;">Nessuna pratica nel periodo selezionato.</p>'
    : `<table>
        <thead><tr>
          <th style="width:90px;">N. Pratica</th>
          <th style="width:75px;">Data</th>
          <th>Oggetto</th>
          <th style="width:110px;">Tipo</th>
          <th style="width:100px;">Ente</th>
          <th style="width:90px;">Stato</th>
        </tr></thead>
        <tbody>
          ${practices.items.map(p => `<tr>
            <td>${p.caseNumber || "—"}</td>
            <td>${fmtDate(p.openingDate)}</td>
            <td>${(p.subject || "—").replace(/</g, "&lt;")}</td>
            <td>${p.practiceType || "—"}</td>
            <td>${p.enteCoinvolto || "—"}</td>
            <td>${p.status || (p.isClosed ? "Chiusa" : "In corso")}</td>
          </tr>`).join("")}
        </tbody>
      </table>`}

  <script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
</body></html>`;

    const w = window.open("", "_blank");
    if (!w) { alert("Popup bloccato dal browser. Abilita le finestre popup per stampare la scheda."); return; }
    w.document.open();
    w.document.write(printContent);
    w.document.close();
  }, [clientReport, selectedClientId, clients, periodLabel]);

  const filteredInvClients = useMemo(() => {
    if (!invClientSearch.trim()) return clientInvData;
    const q = invClientSearch.toLowerCase();
    return clientInvData.filter(c => (c.clientName || "").toLowerCase().includes(q));
  }, [clientInvData, invClientSearch]);

  const filteredPracClients = useMemo(() => {
    if (!pracClientSearch.trim()) return clientPracData;
    const q = pracClientSearch.toLowerCase();
    return clientPracData.filter(c => (c.clientName || "").toLowerCase().includes(q));
  }, [clientPracData, pracClientSearch]);

  // Paginazione globale per le 4 tabelle del Rapporto.
  // - `detail-*` per il drill-down (pratica vs fattura): condividono lo
  //   stesso array `detailData` perché la vista mostra solo una alla volta.
  // - `inv-clients` per la breakdown "Fatturazione per Cliente".
  // - `prac-clients` per la breakdown "Pratiche per Cliente".
  const {
    paged: pagedDetailData,
    paginationBar: detailPaginationBar,
  } = usePagination(detailData, {
    storageKey: "report-detail",
    itemLabelSingular: "voce",
    itemLabelPlural: "voci",
  });
  const {
    paged: pagedInvClients,
    paginationBar: invClientsPaginationBar,
  } = usePagination(filteredInvClients, {
    storageKey: "report-inv-clients",
    itemLabelSingular: "cliente",
    itemLabelPlural: "clienti",
  });
  const {
    paged: pagedPracClients,
    paginationBar: pracClientsPaginationBar,
  } = usePagination(filteredPracClients, {
    storageKey: "report-prac-clients",
    itemLabelSingular: "cliente",
    itemLabelPlural: "clienti",
  });

  // Drill-down openers
  const openInvoiceDrill = async (title, extraFilters = {}) => {
    setDetailLoading(true);
    setDrillDown({ title, type: "invoice" });
    try {
      const list = await api.getInvoicesDetail({ ...activeFilters(), ...extraFilters });
      setDetailData(list);
    } catch { setDetailData([]); }
    finally { setDetailLoading(false); }
  };

  const openPracticeDrill = async (title, extraFilters = {}) => {
    setDetailLoading(true);
    setDrillDown({ title, type: "practice" });
    try {
      const list = await api.getPracticesDetail({ ...activeFilters(), ...extraFilters });
      setDetailData(list);
    } catch { setDetailData([]); }
    finally { setDetailLoading(false); }
  };

  const filterLabel = [
    year !== "all" ? year : null,
    month !== "all" ? MONTHS.find(m => m.value === month)?.label : null,
    management !== "all" ? (management === "agenzia" ? "Agenzia" : "Studio") : null,
  ].filter(Boolean).join(" / ") || "Tutti";

  // === DETAIL VIEW ===
  if (drillDown) {
    const isPractice = drillDown.type === "practice";
    return (
      <div className="space-y-4" data-testid="report-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setDrillDown(null)} data-testid="detail-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Riepilogo
          </Button>
          <h2 className="text-lg font-semibold text-gray-800">{drillDown.title}</h2>
          <Badge variant="outline" className="text-xs">{filterLabel}</Badge>
        </div>
        {detailLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
        ) : isPractice ? (
          /* Practice detail table */
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[100px]">N. Pratica</TableHead>
                  <TableHead className="w-[90px]">Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Oggetto</TableHead>
                  <TableHead className="w-[120px]">Ente</TableHead>
                  <TableHead className="w-[110px]">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailData.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">Nessuna pratica trovata</TableCell></TableRow>
                ) : pagedDetailData.map((p, i) => (
                  <TableRow key={p.id || i} className="hover:bg-blue-50/30" data-testid={`detail-row-${i}`}>
                    <TableCell className="font-mono text-sm font-medium">{p.caseNumber}</TableCell>
                    <TableCell className="text-sm">{fmtDate(p.openingDate)}</TableCell>
                    <TableCell className="text-sm">{p.clientName}</TableCell>
                    <TableCell className="text-sm text-gray-700 max-w-[250px] truncate">{p.subject}</TableCell>
                    <TableCell className="text-xs">{p.enteCoinvolto}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{p.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {detailData.length > 0 && (
              <div className="p-3 border-t bg-gray-50 text-sm text-gray-600">{detailData.length} pratiche</div>
            )}
            <div className="px-3 pb-2">{detailPaginationBar}</div>
          </Card>
        ) : (
          /* Invoice detail table */
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[100px]">N. Fattura</TableHead>
                  <TableHead className="w-[90px]">Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="w-[90px]">Gestione</TableHead>
                  <TableHead className="w-[100px] text-right">Totale</TableHead>
                  <TableHead className="w-[100px] text-right">Incassato</TableHead>
                  <TableHead className="w-[100px] text-right">Residuo</TableHead>
                  <TableHead className="w-[100px]">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailData.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">Nessuna fattura trovata</TableCell></TableRow>
                ) : detailData.map((inv, i) => {
                  const sb = STATUS_BADGE[inv.paymentStatus] || STATUS_BADGE.non_pagata;
                  return (
                    <TableRow key={inv.id || i} className="hover:bg-blue-50/30" data-testid={`detail-row-${i}`}>
                      <TableCell className="font-mono text-sm font-medium">{inv.invoiceNumber}</TableCell>
                      <TableCell className="text-sm">{fmtDate(inv.invoiceDate)}</TableCell>
                      <TableCell className="text-sm">{inv.clientName}</TableCell>
                      <TableCell className="text-xs capitalize">{inv.managementType}</TableCell>
                      <TableCell className="text-sm text-right font-medium">{fmt(inv.totale)} &euro;</TableCell>
                      <TableCell className="text-sm text-right text-green-700">{fmt(inv.totalPaid)} &euro;</TableCell>
                      <TableCell className="text-sm text-right text-amber-700">{fmt(inv.residuo)} &euro;</TableCell>
                      <TableCell><Badge className={`${sb.cls} text-xs`}>{sb.label}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {detailData.length > 0 && (
              <div className="p-3 border-t bg-gray-50 text-sm text-gray-600 flex justify-between">
                <span>{detailData.length} fatture</span>
                <span>
                  Totale: <b>{fmt(detailData.reduce((s, i) => s + i.totale, 0))} &euro;</b>
                  {" | "}Incassato: <b className="text-green-700">{fmt(detailData.reduce((s, i) => s + i.totalPaid, 0))} &euro;</b>
                  {" | "}Residuo: <b className="text-amber-700">{fmt(detailData.reduce((s, i) => s + i.residuo, 0))} &euro;</b>
                </span>
              </div>
            )}
            <div className="px-3 pb-2">{detailPaginationBar}</div>
          </Card>
        )}
      </div>
    );
  }

  // === SUMMARY VIEW ===
  return (
    <div className="space-y-5" data-testid="report-tab">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-gray-800 mr-2 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-slate-600" />
          Report Cliente
        </h2>
        {isAdmin && (
          <>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-[110px] h-9" data-testid="report-year-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[140px] h-9" data-testid="report-month-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={management} onValueChange={setManagement}>
              <SelectTrigger className="w-[130px] h-9" data-testid="report-mgmt-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                <SelectItem value="studio">Studio</SelectItem>
                <SelectItem value="agenzia">Agenzia</SelectItem>
              </SelectContent>
            </Select>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
          </>
        )}
      </div>

      {/* ==================== CLIENT REPORT SECTION ==================== */}
      {/* 27/02/2026 — Riga unica compatta: combobox cliente + periodo + azioni.
          Accessibile a tutti i ruoli (operatori, responsabili, admin). */}
      <Card data-testid="client-report-picker-card">
        <CardContent className="p-3">
          <div className="flex items-end gap-2 flex-wrap">
            {/* Combobox cliente single-line searchable */}
            <div className="flex-1 min-w-[260px] max-w-md">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1 block">Cliente</label>
              <SearchableCombobox
                items={clientComboItems}
                value={selectedClientId}
                onValueChange={(v) => setSelectedClientId(v || "")}
                placeholder="Cerca / seleziona cliente…"
                searchPlaceholder="Cerca per nome, P.IVA, C.F…"
                emptyMessage="Nessun cliente trovato."
                data-testid="client-report-combobox"
              />
            </div>
            {/* Mese */}
            <div className="w-[140px]">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1 block">Mese</label>
              <Select value={crMonth} onValueChange={setCrMonth}>
                <SelectTrigger className="h-9 text-sm" data-testid="client-report-month"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* Anno */}
            <div className="w-[120px]">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1 block">Anno</label>
              <Select value={crYear} onValueChange={setCrYear}>
                <SelectTrigger className="h-9 text-sm" data-testid="client-report-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Dal */}
            <div className="w-[140px]">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1 block">Dal</label>
              <Input
                type="date"
                value={crFromInput}
                onChange={(e) => setCrFromInput(e.target.value)}
                className="h-9 text-sm"
                data-testid="client-report-from"
              />
            </div>
            {/* Al */}
            <div className="w-[140px]">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1 block">Al</label>
              <Input
                type="date"
                value={crToInput}
                onChange={(e) => setCrToInput(e.target.value)}
                className="h-9 text-sm"
                data-testid="client-report-to"
              />
            </div>
            {/* Apply filter */}
            <Button
              variant="default"
              size="sm"
              className="h-9 bg-blue-600 hover:bg-blue-700"
              onClick={applyPeriod}
              disabled={!selectedClientId}
              data-testid="client-report-apply"
            >
              <Filter className="h-4 w-4 mr-1" />
              Applica filtro
            </Button>
            {(appliedPeriod.from || appliedPeriod.to || crFromInput || crToInput || crMonth !== "all" || crYear !== "all") && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-slate-600"
                onClick={resetPeriod}
                data-testid="client-report-reset-period"
              >
                Reset
              </Button>
            )}
            {/* Print */}
            <Button
              variant="outline"
              size="sm"
              className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={handlePrintClientReport}
              disabled={!clientReport}
              data-testid="client-report-print"
              title="Stampa la scheda del cliente con i filtri attivi"
            >
              <Printer className="h-4 w-4 mr-1" />
              Stampa scheda cliente
            </Button>
            {clientReportLoading && <Loader2 className="h-4 w-4 animate-spin text-blue-500 mb-2" />}
          </div>
          {/* Period label hint */}
          {selectedClientId && (
            <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-2" data-testid="client-report-period-label">
              <span className="font-semibold uppercase tracking-wide text-slate-400">Periodo applicato:</span>
              <Badge variant="outline" className="text-[10px]">{periodLabel}</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI block + practices list (solo dopo aver selezionato un cliente) */}
      {selectedClientId && clientReport && (
        <ClientReportView report={clientReport} />
      )}
      {selectedClientId && !clientReport && !clientReportLoading && (
        <Card><CardContent className="p-6 text-center text-sm text-gray-500">
          Nessun dato disponibile per il cliente selezionato.
        </CardContent></Card>
      )}

      {/* ==================== INVOICE SECTION ==================== */}
      {invoiceData && (
        <>
          <h3 className="text-base font-semibold text-gray-700 border-b pb-1">Fatturazione</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoiceDrill("Tutte le fatture")} data-testid="card-fatturato">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Totale Fatturato</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{fmt(invoiceData.totaleFatturato)} &euro;</p>
                  </div>
                  <Euro className="h-8 w-8 text-blue-200" />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-400">{invoiceData.totalInvoices} fatture</p>
                  <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoiceDrill("Fatture con incassi", { hasPayments: true })} data-testid="card-incassato">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Totale Incassato</p>
                    <p className="text-2xl font-bold text-green-700 mt-1">{fmt(invoiceData.totaleIncassato)} &euro;</p>
                  </div>
                  <CreditCard className="h-8 w-8 text-green-200" />
                </div>
                <div className="flex items-center justify-between mt-2">
                  {invoiceData.totaleFatturato > 0 ? <p className="text-xs text-gray-400">{Math.round((invoiceData.totaleIncassato / invoiceData.totaleFatturato) * 100)}% del fatturato</p> : <p className="text-xs">&nbsp;</p>}
                  <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoiceDrill("Fatture con residuo", { hasResiduo: true })} data-testid="card-residuo">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Totale Residuo</p>
                    <p className="text-2xl font-bold text-amber-700 mt-1">{fmt(invoiceData.totaleResiduo)} &euro;</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-200" />
                </div>
                <div className="flex items-center justify-between mt-2"><p className="text-xs">&nbsp;</p><ChevronRight className="h-3.5 w-3.5 text-gray-300" /></div>
              </CardContent>
            </Card>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoiceDrill("Fatture Pagate", { paymentStatus: "pagata" })} data-testid="card-pagate">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-green-50 flex items-center justify-center shrink-0"><CheckCircle2 className="h-5 w-5 text-green-600" /></div>
                <div className="flex-1"><p className="text-xs text-gray-500 uppercase">Pagate</p><p className="text-xl font-bold">{invoiceData.countPagate}</p></div>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoiceDrill("Fatture Parziali", { paymentStatus: "parziale" })} data-testid="card-parziali">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-orange-50 flex items-center justify-center shrink-0"><AlertCircle className="h-5 w-5 text-orange-500" /></div>
                <div className="flex-1"><p className="text-xs text-gray-500 uppercase">Parziali</p><p className="text-xl font-bold">{invoiceData.countParziali}</p></div>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openInvoiceDrill("Fatture Non Pagate", { paymentStatus: "non_pagata" })} data-testid="card-non-pagate">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center shrink-0"><MinusCircle className="h-5 w-5 text-red-500" /></div>
                <div className="flex-1"><p className="text-xs text-gray-500 uppercase">Non Pagate</p><p className="text-xl font-bold">{invoiceData.countNonPagate}</p></div>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </CardContent>
            </Card>
          </div>
          {/* Invoice client breakdown */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Users className="h-4 w-4 text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-700">Fatturazione per Cliente</h4>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <Input placeholder="Cerca cliente..." value={invClientSearch} onChange={e => setInvClientSearch(e.target.value)} className="pl-8 h-9 text-sm" data-testid="inv-client-search" />
              </div>
            </div>
            <Card className="overflow-hidden" data-testid="inv-client-table">
              <Table>
                <TableHeader><TableRow className="bg-gray-50">
                  <TableHead>Cliente</TableHead><TableHead className="w-[50px] text-center">N.</TableHead>
                  <TableHead className="w-[110px] text-right">Fatturato</TableHead><TableHead className="w-[110px] text-right">Incassato</TableHead><TableHead className="w-[110px] text-right">Residuo</TableHead><TableHead className="w-[30px]"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filteredInvClients.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-400">Nessun dato</TableCell></TableRow>
                  ) : pagedInvClients.map((c, i) => (
                    <TableRow key={c.clientId || i} className="cursor-pointer hover:bg-blue-50/50" onClick={() => openInvoiceDrill(`Fatture: ${c.clientName}`, { clientId: c.clientId })} data-testid={`inv-client-row-${i}`}>
                      <TableCell className="font-medium text-sm">{c.clientName || "—"}</TableCell>
                      <TableCell className="text-center text-sm text-gray-500">{c.count}</TableCell>
                      <TableCell className="text-sm text-right font-medium">{fmt(c.fatturato)} &euro;</TableCell>
                      <TableCell className="text-sm text-right text-green-700">{fmt(c.incassato)} &euro;</TableCell>
                      <TableCell className="text-sm text-right text-amber-700">{fmt(c.residuo)} &euro;</TableCell>
                      <TableCell><ChevronRight className="h-3.5 w-3.5 text-gray-300" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="px-3 pb-2">{invClientsPaginationBar}</div>
            </Card>
          </div>
        </>
      )}

      {/* ==================== PRACTICE SECTION ==================== */}
      {practiceData && (
        <>
          <h3 className="text-base font-semibold text-gray-700 border-b pb-1 mt-6">Pratiche</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-blue-400" onClick={() => openPracticeDrill("Pratiche Aperte", { statusFilter: "aperte" })} data-testid="card-prac-aperte">
              <CardContent className="p-4 flex items-center gap-3">
                <FolderOpen className="h-8 w-8 text-blue-300" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 uppercase">Aperte</p>
                  <p className="text-2xl font-bold text-gray-900">{practiceData.aperte}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-green-400" onClick={() => openPracticeDrill("Pratiche Chiuse", { statusFilter: "chiuse" })} data-testid="card-prac-chiuse">
              <CardContent className="p-4 flex items-center gap-3">
                <FolderClosed className="h-8 w-8 text-green-300" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 uppercase">Chiuse</p>
                  <p className="text-2xl font-bold text-gray-900">{practiceData.chiuse}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-orange-400" onClick={() => openPracticeDrill("Da Fatturare", { statusFilter: "da_fatturare" })} data-testid="card-prac-da-fatturare">
              <CardContent className="p-4 flex items-center gap-3">
                <FileWarning className="h-8 w-8 text-orange-300" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 uppercase">Da Fatturare</p>
                  <p className="text-2xl font-bold text-gray-900">{practiceData.daFatturare}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-teal-400" onClick={() => openPracticeDrill("Pratiche Fatturate", { statusFilter: "fatturate" })} data-testid="card-prac-fatturate">
              <CardContent className="p-4 flex items-center gap-3">
                <FileCheck2 className="h-8 w-8 text-teal-300" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 uppercase">Fatturate</p>
                  <p className="text-2xl font-bold text-gray-900">{practiceData.fatturate}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </CardContent>
            </Card>
          </div>

          {/* Practice client breakdown */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Users className="h-4 w-4 text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-700">Pratiche per Cliente</h4>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <Input placeholder="Cerca cliente..." value={pracClientSearch} onChange={e => setPracClientSearch(e.target.value)} className="pl-8 h-9 text-sm" data-testid="prac-client-search" />
              </div>
            </div>
            <Card className="overflow-hidden" data-testid="prac-client-table">
              <Table>
                <TableHeader><TableRow className="bg-gray-50">
                  <TableHead>Cliente</TableHead><TableHead className="w-[50px] text-center">Tot.</TableHead>
                  <TableHead className="w-[70px] text-center">Aperte</TableHead><TableHead className="w-[70px] text-center">Chiuse</TableHead>
                  <TableHead className="w-[80px] text-center">Da Fatt.</TableHead><TableHead className="w-[80px] text-center">Fatturate</TableHead>
                  <TableHead className="w-[30px]"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filteredPracClients.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-6 text-gray-400">Nessun dato</TableCell></TableRow>
                  ) : pagedPracClients.map((c, i) => (
                    <TableRow key={c.clientId || i} className="cursor-pointer hover:bg-blue-50/50" onClick={() => openPracticeDrill(`Pratiche: ${c.clientName}`, { clientId: c.clientId })} data-testid={`prac-client-row-${i}`}>
                      <TableCell className="font-medium text-sm">{c.clientName || "—"}</TableCell>
                      <TableCell className="text-center text-sm font-medium">{c.count}</TableCell>
                      <TableCell className="text-center text-sm text-blue-600">{c.aperte}</TableCell>
                      <TableCell className="text-center text-sm text-green-600">{c.chiuse}</TableCell>
                      <TableCell className="text-center text-sm text-orange-600">{c.daFatturare}</TableCell>
                      <TableCell className="text-center text-sm text-teal-600">{c.fatturate}</TableCell>
                      <TableCell><ChevronRight className="h-3.5 w-3.5 text-gray-300" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredPracClients.length > 0 && (
                <div className="p-3 border-t bg-gray-50 text-sm text-gray-600">
                  {filteredPracClients.length} clienti — Totale: {filteredPracClients.reduce((s, c) => s + c.count, 0)} pratiche
                </div>
              )}
              <div className="px-3 pb-2">{pracClientsPaginationBar}</div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ClientReportView — Riepilogo operativo cliente (27/02/2026)
// KPI cards + ultima interazione + operatori + tabella pratiche.
// ─────────────────────────────────────────────────────────────────────
function ClientReportView({ report }) {
  const practices = report?.practices || { total: 0, open: 0, closed: 0, items: [] };
  const diary = report?.dailyDiary || { totalNotes: 0, lastNoteDate: null, lastNote: null };
  const work = report?.periodicWork || { totalActivities: 0, lastActivityDate: null, lastActivity: null };
  const operators = Array.isArray(report?.operators) ? report.operators : [];
  const lastInter = report?.lastInteraction || { date: null, source: null };
  const lastInterLabel =
    lastInter.source === "diary" ? "Diario Giornaliero"
    : lastInter.source === "daily" ? "Lavoro Quotidiano"
    : null;

  return (
    <div className="space-y-4" data-testid="client-report-view">
      {/* Header cliente */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <UserCircle2 className="h-5 w-5 text-blue-600" />
          {report.clientName || "Cliente"}
        </h3>
        {lastInter.date && (
          <Badge className="bg-blue-50 text-blue-700 border border-blue-200" data-testid="client-report-last-interaction">
            <Activity className="h-3 w-3 mr-1" />
            Ultima interazione: {fmtDate(lastInter.date)}{lastInterLabel ? ` — ${lastInterLabel}` : ""}
          </Badge>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-l-4 border-l-slate-400" data-testid="client-kpi-total">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase">Pratiche totali</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{practices.total}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500" data-testid="client-kpi-open">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase">In corso</p>
            <p className="text-2xl font-bold text-blue-700 mt-1">{practices.open}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500" data-testid="client-kpi-closed">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase">Concluse</p>
            <p className="text-2xl font-bold text-green-700 mt-1">{practices.closed}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500" data-testid="client-kpi-diary">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> Note Diario
            </p>
            <p className="text-2xl font-bold text-amber-700 mt-1">{diary.totalNotes}</p>
            <p className="text-xs text-gray-400 mt-1">
              {diary.lastNoteDate ? `Ultima: ${fmtDate(diary.lastNoteDate)}` : "—"}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500" data-testid="client-kpi-daily">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
              <ClipboardList className="h-3 w-3" /> Lavoro Quotidiano
            </p>
            <p className="text-2xl font-bold text-purple-700 mt-1">{work.totalActivities}</p>
            <p className="text-xs text-gray-400 mt-1">
              {work.lastActivityDate ? `Ultima: ${fmtDate(work.lastActivityDate)}` : "—"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Ultima nota Diario + Ultima attività Lavoro Quotidiano */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card data-testid="client-last-note-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-amber-600" />
              <h4 className="text-sm font-semibold text-gray-700">Ultima nota Diario</h4>
            </div>
            {diary.lastNote ? (
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{fmtDate(diary.lastNote.date)}</span>
                  {diary.lastNote.time && <span>{diary.lastNote.time}</span>}
                  {diary.lastNote.contactType && <Badge variant="outline" className="text-[10px]">{diary.lastNote.contactType}</Badge>}
                </div>
                <p className="text-gray-800">{diary.lastNote.content || "—"}</p>
                {diary.lastNote.operatorName && (
                  <p className="text-xs text-gray-500">Operatore: {diary.lastNote.operatorName}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Nessuna nota registrata.</p>
            )}
          </CardContent>
        </Card>
        <Card data-testid="client-last-activity-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="h-4 w-4 text-purple-600" />
              <h4 className="text-sm font-semibold text-gray-700">Ultima attività Lavoro Quotidiano</h4>
            </div>
            {work.lastActivity ? (
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{fmtDate(work.lastActivity.date)}</span>
                  {work.lastActivity.time && <span>{work.lastActivity.time}</span>}
                  {work.lastActivity.operationalArea && <Badge variant="outline" className="text-[10px]">{work.lastActivity.operationalArea}</Badge>}
                  {work.lastActivity.status && <Badge variant="outline" className="text-[10px] capitalize">{work.lastActivity.status}</Badge>}
                </div>
                <p className="text-gray-800">{work.lastActivity.description || "—"}</p>
                {work.lastActivity.operatorName && (
                  <p className="text-xs text-gray-500">Operatore: {work.lastActivity.operatorName}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Nessuna attività registrata.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Operatori coinvolti */}
      {operators.length > 0 && (
        <Card data-testid="client-operators-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-slate-500" />
              <h4 className="text-sm font-semibold text-gray-700">Operatori coinvolti ({operators.length})</h4>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {operators.map((o) => (
                <Badge key={o.id || o.name} variant="outline" className="text-xs">{o.name}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista pratiche del cliente */}
      <Card className="overflow-hidden" data-testid="client-practices-table">
        <div className="p-3 border-b bg-gray-50 text-sm font-semibold text-gray-700 flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-slate-500" />
          Pratiche del cliente ({practices.total})
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-[110px]">N. Pratica</TableHead>
              <TableHead className="w-[90px]">Data</TableHead>
              <TableHead>Oggetto</TableHead>
              <TableHead className="w-[140px]">Tipo</TableHead>
              <TableHead className="w-[120px]">Ente</TableHead>
              <TableHead className="w-[110px]">Stato</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {practices.items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-400">Nessuna pratica per questo cliente.</TableCell></TableRow>
            ) : practices.items.map((p, i) => (
              <TableRow key={p.id || i} className="hover:bg-blue-50/30" data-testid={`client-practice-row-${i}`}>
                <TableCell className="font-mono text-sm font-medium">{p.caseNumber || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDate(p.openingDate)}</TableCell>
                <TableCell className="text-sm text-gray-700 max-w-[280px] truncate">{p.subject || "—"}</TableCell>
                <TableCell className="text-xs text-gray-600">{p.practiceType || "—"}</TableCell>
                <TableCell className="text-xs">{p.enteCoinvolto || "—"}</TableCell>
                <TableCell>
                  <Badge className={p.isClosed ? "bg-green-100 text-green-700 text-xs" : "bg-blue-100 text-blue-700 text-xs"}>
                    {p.status || (p.isClosed ? "Chiusa" : "In corso")}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}


export default React.memo(ReportTab);
